import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  CloudDone,
  CloudOff,
  Delete,
  ExitToApp,
  Hotel,
  LocalHospital,
  Lock,
  Person,
  PersonAdd,
  Settings,
  SwapHoriz,
  Sync,
  Tv,
  WarningAmber,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import SwitchUserDialog from './SwitchUserDialog'
import TreatmentZoneCard from './TreatmentZoneCard'
import ZoneDrawer from './ZoneDrawer'
import WaitingRoomCard from './WaitingRoomCard'
import API from '../../../services/API'
import EmployeeSelector from '../../events/EmployeeSelector'
import { UserContext } from '../../../App'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import useIsMobile from '../../../helpers/useIsMobile'

export const demoUsers = [
  { id: '1', name: 'Sarah Jones', role: 'Paramedic' },
  { id: '2', name: 'Tom Williams', role: 'Nurse' },
  { id: '3', name: 'Aisha Patel', role: 'Doctor' },
]

const templateZones = [
  {
    id: 'z1',
    name: 'Resus Bay 1',
    type: 'Resus',
    status: 'Available',
    capacity: 1,
    patients: [],
  },
  {
    id: 'z2',
    name: 'Resus Bay 2',
    type: 'Resus',
    status: 'Available',
    capacity: 1,
    patients: [],
  },
  {
    id: 'z3',
    name: 'Minor Injury Bay 1',
    type: 'Treatment Area',
    status: 'Available',
    capacity: 1,
    patients: [],
  },
  {
    id: 'z4',
    name: 'Minor Injury Bay 2',
    type: 'Treatment Area',
    status: 'Cleaning',
    capacity: 1,
    patients: [],
  },
  {
    id: 'z5',
    name: 'Waiting Room',
    type: 'Waiting',
    status: 'Available',
    capacity: 999,
    patients: [],
  },
]

const zoneTypes = [
  'Waiting Room',
  'Bed',
  'Chair',
  'Triage',
  'Treatment Area',
  'Resus',
  'Awaiting Transport',
  'Discharge Area',
  'Other',
]

const zoneStatuses = ['Available', 'Cleaning', 'Out of Order']
const WORKSTATION_LOCK_SECONDS = 120

const syncConfig = {
  live: {
    icon: <CloudDone fontSize="small" />,
    label: 'Live',
    color: '#4ade80',
  },
  delayed: {
    icon: <WarningAmber fontSize="small" />,
    label: 'Delayed',
    color: '#fbbf24',
  },
  offline: {
    icon: <CloudOff fontSize="small" />,
    label: 'Offline',
    color: '#f87171',
  },
  connecting: {
    icon: <Sync fontSize="small" className="spin" />,
    label: 'Connecting',
    color: '#93c5fd',
  },
}

const centreStatusColor = {
  Open: 'success',
  Standby: 'warning',
  Closed: 'default',
}

const emptyZoneForm = {
  name: '',
  type: 'Bed',
  capacityType: 'Single',
  maxCapacity: 1,
  status: 'Available',
}

const emptyCentreForm = {
  name: '',
  location: '',
  what3words: '',
  status: 'Open',
  assignedStaff: [],
}

const staffUserId = (staff) =>
  staff?.user?._id || staff?.user || staff?.userAccount?._id

const staffName = (staff) => {
  const user = staff?.user || staff?.userAccount
  return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
}

const normaliseAssignedStaff = (staff) => {
  const user = staff?.user || staff
  const id = staffUserId(staff) || user?._id || staff?.id
  return {
    id,
    _id: id,
    name: staffName(staff) || staff?.name || 'Staff member',
    role: staff?.role || 'Clinician',
    photograph: user?.photograph || staff?.photograph,
    email: user?.email || staff?.email,
    user,
  }
}

const ConfigureCentreDialog = ({
  open,
  onClose,
  centre,
  onSaved,
}) => {
  const isPhone = useIsMobile('sm')
  const [form, setForm] = useState(emptyCentreForm)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [selectedRole, setSelectedRole] = useState('Clinician')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setForm({
        name: centre?.title ?? '',
        location: centre?.location ?? '',
        what3words: centre?.what3words ?? '',
        status: centre?.status ?? 'Open',
        assignedStaff: centre?.assignedStaff ?? [],
      })
    } else {
      setForm(emptyCentreForm)
      setSelectedStaff(null)
      setSelectedRole('Clinician')
    }
  }, [open, centre])

  const addAssignedStaff = () => {
    if (!selectedStaff?.userAccount?._id) return

    setForm((prev) => ({
      ...prev,
      assignedStaff: [
        ...(prev.assignedStaff || []).filter(
          (staff) => staffUserId(staff) !== selectedStaff.userAccount._id,
        ),
        {
          user: selectedStaff.userAccount,
          role: selectedRole,
        },
      ],
    }))

    setSelectedStaff(null)
    setSelectedRole('Clinician')
  }

  const removeAssignedStaff = (userId) => {
    setForm((prev) => ({
      ...prev,
      assignedStaff: (prev.assignedStaff || []).filter(
        (staff) => staffUserId(staff) !== userId,
      ),
    }))
  }

  const saveCentre = async () => {
    if (!centre?.id || !form.name || saving) return

    setSaving(true)

    try {
      const res = await API.post(`/commandcontrol/treatment-centres/${centre.id}`, {
        ...form,
        assignedStaff: (form.assignedStaff || []).map((staff) => ({
          user: staffUserId(staff),
          role: staff.role || 'Clinician',
        })),
      })

      if (res.status === 200) {
        onSaved?.()
        onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isPhone}
    >
      <DialogTitle>Configure Centre</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Centre name"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Status"
                select
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
                fullWidth
              >
                {['Open', 'Standby', 'Closed'].map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, location: e.target.value }))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="What3Words"
                value={form.what3words}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, what3words: e.target.value }))
                }
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider />

          <Stack spacing={1.5}>
            <Typography fontWeight={900}>Assigned Staff</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <EmployeeSelector
                value={selectedStaff}
                onChange={(e, value) => setSelectedStaff(value)}
                valueType="employee"
                label="Staff member"
                fullWidth
              />

              <TextField
                label="Role"
                select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{ minWidth: { xs: 0, md: 150 } }}
                fullWidth={isPhone}
              >
                {['Clinician', 'Manager'].map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="outlined"
                onClick={addAssignedStaff}
                disabled={!selectedStaff}
                sx={{ minWidth: 90 }}
                fullWidth={isPhone}
              >
                Add
              </Button>
            </Stack>

            {(form.assignedStaff || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No staff assigned yet.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {form.assignedStaff.map((staff) => (
                  <Chip
                    key={staffUserId(staff)}
                    avatar={<Avatar src={staff.user?.photograph} />}
                    label={`${staffName(staff) || 'Staff member'} - ${
                      staff.role || 'Clinician'
                    }`}
                    onDelete={() => removeAssignedStaff(staffUserId(staff))}
                    sx={{ borderRadius: 999, fontWeight: 800 }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={saveCentre} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ConfigureZonesDialog = ({
  open,
  onClose,
  locationID,
  zones,
  onSaved,
}) => {
  const isPhone = useIsMobile('sm')
  const [form, setForm] = useState(emptyZoneForm)
  const [saving, setSaving] = useState(false)
  const [deletingZone, setDeletingZone] = useState(null)

  useEffect(() => {
    if (!open) setForm(emptyZoneForm)
  }, [open])

  const saveNewZone = async () => {
    if (!form.name || !locationID || saving) return

    setSaving(true)

    try {
      const res = await API.post(
        `/commandcontrol/treatment-locations/${locationID}/zones`,
        form,
      )

      if (res.status === 200) {
        setForm(emptyZoneForm)
        onSaved?.()
      }
    } finally {
      setSaving(false)
    }
  }

  const updateZone = async (zone, patch) => {
    const zoneID = zone._id || zone.id
    if (!zoneID) return

    const res = await API.post(`/commandcontrol/treatment-zones/${zoneID}`, {
      ...patch,
    })

    if (res.status === 200) {
      onSaved?.()
    }
  }

  const deleteZone = async (zone) => {
    const zoneID = zone._id || zone.id
    if (!zoneID || deletingZone) return

    const confirmed = window.confirm(
      `Delete ${zone.name}? This is only possible when no patients are assigned to the area.`,
    )

    if (!confirmed) return

    setDeletingZone(zoneID)

    try {
      const res = await API.delete(
        `/commandcontrol/treatment-zones/${zoneID}`,
        { skipGlobalErrorHandler: true },
      )

      if (res.status === 200) {
        onSaved?.()
        return
      }

      window.alert(res.data?.error || 'Unable to delete this area.')
    } finally {
      setDeletingZone(null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isPhone}
    >
      <DialogTitle>Configure Zones</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Zone name"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Type"
                select
                value={form.type}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, type: e.target.value }))
                }
                fullWidth
              >
                {zoneTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={2}>
              <TextField
                label="Capacity"
                type="number"
                value={form.maxCapacity}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    maxCapacity: Number(e.target.value),
                    capacityType: Number(e.target.value) > 1 ? 'Multiple' : 'Single',
                  }))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <Button
                variant="contained"
                onClick={saveNewZone}
                disabled={!form.name || saving}
                fullWidth
                sx={{ height: '100%' }}
              >
                {saving ? 'Saving...' : 'Add Zone'}
              </Button>
            </Grid>
          </Grid>

          <Divider />

          {zones.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No zones configured yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {zones.map((zone) => (
                <Paper
                  key={zone._id || zone.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: '1px solid rgba(15,23,42,0.08)',
                  }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography fontWeight={900}>{zone.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {zone.type} - capacity {zone.capacity || zone.maxCapacity || 1}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Status"
                        select
                        size="small"
                        value={
                          zone.status === 'Occupied'
                            ? 'Available'
                            : zone.status || 'Available'
                        }
                        onChange={(e) =>
                          updateZone(zone, { status: e.target.value })
                        }
                        fullWidth
                      >
                        {zoneStatuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={8} md={3}>
                      <TextField
                        label="Capacity"
                        type="number"
                        size="small"
                        value={zone.capacity || zone.maxCapacity || 1}
                        onChange={(e) =>
                          updateZone(zone, {
                            maxCapacity: Number(e.target.value),
                            capacityType:
                              Number(e.target.value) > 1 ? 'Multiple' : 'Single',
                          })
                        }
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={4} md={2}>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<Delete />}
                        onClick={() => deleteZone(zone)}
                        disabled={
                          deletingZone === (zone._id || zone.id) ||
                          (zone.patients?.length || 0) > 0
                        }
                        fullWidth
                      >
                        {deletingZone === (zone._id || zone.id)
                          ? 'Deleting...'
                          : 'Delete'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const patientFromAssignment = (assignment) => ({
  id: assignment.id,
  epcrID: assignment.epcr?.id,
  name: assignment.epcr
    ? [
        assignment.epcr.demographics?.firstName,
        assignment.epcr.demographics?.lastName,
      ]
        .filter(Boolean)
        .join(' ') ||
      (assignment.epcr.demographics?.lastNameUnknown
        ? 'Unknown Patient'
        : 'Unnamed Patient')
    : assignment.incident?.caller?.name ||
      `Incident ${assignment.incident?.incidentNumber ?? ''}`.trim(),
  incidentNumber:
    assignment.epcr?.incident?.incidentNumber ||
    assignment.incident?.incidentNumber ||
    assignment.epcr?.epcrNumber,
  priority: assignment.incident?.priority || 'Moderate',
  complaint:
    assignment.epcr?.history?.historyPresentingComplaint ||
    assignment.epcr?.history?.presentingComplaint?.join(', ') ||
    assignment.epcr?.incident?.reportedComplaint ||
    assignment.incident?.description ||
    assignment.incident?.type ||
    assignment.incident?.location ||
    'No complaint recorded',
  epcrStatus: assignment.epcr?.review?.status || 'Started',
  arrivedAt: assignment.arrivedAt,
})

const normaliseZone = (zone) => {
  const patients = (zone.patients || []).map((patient) =>
    patient.epcr || patient.incident ? patientFromAssignment(patient) : patient,
  )

  return {
    ...zone,
    id: zone.id || zone._id,
    capacity: zone.capacity || zone.maxCapacity || 1,
    patients,
    status: patients.length > 0 ? 'Occupied' : zone.status || 'Available',
  }
}

const PendingPatientsPanel = ({ assignments, zones, onPlaced }) => {
  const [selectedZones, setSelectedZones] = useState({})
  const [placing, setPlacing] = useState(null)

  const placePatient = async (assignment) => {
    const treatmentZone = selectedZones[assignment.id]
    if (!treatmentZone || placing) return

    const zone = zones.find((item) => item.id === treatmentZone)
    setPlacing(assignment.id)

    try {
      const res = await API.post(
        `/commandcontrol/treatment-assignments/${assignment.id}`,
        {
          treatmentZone,
          status:
            zone?.type === 'Waiting Room' || zone?.type === 'Waiting'
              ? 'Waiting'
              : 'In Treatment',
        },
      )

      if (res.status === 200) {
        setSelectedZones((prev) => {
          const next = { ...prev }
          delete next[assignment.id]
          return next
        })
        onPlaced?.()
      }
    } finally {
      setPlacing(null)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        border: '1px solid rgba(35,154,164,0.22)',
        backgroundColor: '#F8FEFF',
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography fontWeight={900}>Pending Patients</Typography>
            <Typography variant="body2" color="text.secondary">
              Newly transferred ePCRs waiting for placement.
            </Typography>
          </Box>

          <Chip
            label={`${assignments.length} pending`}
            color={assignments.length ? 'warning' : 'default'}
            sx={{ borderRadius: 999, fontWeight: 900 }}
          />
        </Stack>

        {assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No patients are waiting to be placed.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {assignments.map((assignment) => {
              const patient = patientFromAssignment(assignment)
              return (
                <Paper
                  key={assignment.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(15,23,42,0.08)',
                    backgroundColor: 'white',
                  }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar sx={{ bgcolor: '#239AA4' }}>
                          <Person />
                        </Avatar>
                        <Box minWidth={0}>
                          <Typography fontWeight={900} noWrap>
                            {patient.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {patient.incidentNumber || 'No incident number'} -{' '}
                            {patient.complaint}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        label="Place in"
                        size="small"
                        value={selectedZones[assignment.id] || ''}
                        onChange={(e) =>
                          setSelectedZones((prev) => ({
                            ...prev,
                            [assignment.id]: e.target.value,
                          }))
                        }
                        fullWidth
                        disabled={zones.length === 0}
                      >
                        {zones.map((zone) => (
                          <MenuItem
                            key={zone.id}
                            value={zone.id}
                            disabled={
                              (zone.patients?.length || 0) >=
                              Math.max(Number(zone.capacity || zone.maxCapacity) || 1, 1)
                            }
                          >
                            {zone.name} ({zone.type}){' '}
                            {(zone.patients?.length || 0) >=
                            Math.max(Number(zone.capacity || zone.maxCapacity) || 1, 1)
                              ? '- full'
                              : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Button
                        variant="contained"
                        onClick={() => placePatient(assignment)}
                        disabled={
                          !selectedZones[assignment.id] ||
                          placing === assignment.id ||
                          zones.length === 0
                        }
                        fullWidth
                      >
                        {placing === assignment.id ? 'Placing...' : 'Place'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

const TreatmentCentreWorkspace = ({
  title = 'Main Medical Centre',
  locationID,
  centre,
  status,
  snapshotZones,
  pendingAssignments,
  loading = false,
  canConfigure = false,
  syncState,
  refresh,
  workstationMode = false,
  onExit,
}) => {
  const { user } = useContext(UserContext)
  const navigate = useNavigate()
  const { setExistingRecord, setSiteMode } = useContext(SiteModeContext)
  const [zones, setZones] = useState(
    Array.isArray(snapshotZones)
      ? snapshotZones.map(normaliseZone)
      : templateZones,
  )
  const [selectedZone, setSelectedZone] = useState(null)
  const [openSwitchUser, setOpenSwitchUser] = useState(false)
  const [openConfigureCentre, setOpenConfigureCentre] = useState(false)
  const [openConfigureZones, setOpenConfigureZones] = useState(false)
  const [creatingPatient, setCreatingPatient] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [workstationLocked, setWorkstationLocked] = useState(workstationMode)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [lockCountdown, setLockCountdown] = useState(WORKSTATION_LOCK_SECONDS)
  const [workstationPromptOpen, setWorkstationPromptOpen] = useState(false)
  // Draw the eye to the workstation button when the centre first opens.
  const [pulseWorkstationButton, setPulseWorkstationButton] = useState(true)
  const pendingPatientAssignments = useMemo(
    () => (Array.isArray(pendingAssignments) ? pendingAssignments : []),
    [pendingAssignments],
  )
  const assignedUsers = useMemo(
    () =>
      (centre?.assignedStaff || [])
        .map(normaliseAssignedStaff)
        .filter((staff) => staff.id),
    [centre?.assignedStaff],
  )
  const signedInUser = useMemo(
    () => ({
      id: user?._id,
      _id: user?._id,
      name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'You',
      role: 'Signed in user',
      photograph: user?.photograph,
    }),
    [user],
  )
  const boardLocked = workstationMode && workstationLocked
  const displayUser = workstationMode ? currentUser : signedInUser
  // Same gate as the old Treatment Workstations directory page.
  const canOpenWorkstation =
    user?.security?.isSiteAdmin || user?.employee?.cadAccess === 'Commander'
  const sync = syncConfig[syncState]
  const stats = useMemo(() => {
    const patients = zones.flatMap((zone) => zone.patients || [])
    return {
      totalPatients: patients.length + pendingPatientAssignments.length,
      pending: pendingPatientAssignments.length,
      occupied: zones.filter((z) => z.status === 'Occupied').length,
      available: zones.filter((z) => z.status === 'Available').length,
      cleaning: zones.filter((z) => z.status === 'Cleaning').length,
      outOfOrder: zones.filter((z) => z.status === 'Out of Order').length,
    }
  }, [pendingPatientAssignments.length, zones])

  const waitingZone = zones.find((zone) =>
    ['Waiting', 'Waiting Room'].includes(zone.type),
  )
  const waitingRoomHasCapacity =
    !waitingZone ||
    (waitingZone.patients?.length || 0) <
      Math.max(Number(waitingZone.capacity) || 1, 1)

  const clinicalZones = zones.filter(
    (zone) => !['Waiting', 'Waiting Room'].includes(zone.type),
  )

  useEffect(() => {
    if (Array.isArray(snapshotZones)) {
      setZones(snapshotZones.map(normaliseZone))
    }
  }, [snapshotZones])

  const openEPCR = (epcrID) => {
    if (!epcrID) return

    setExistingRecord(epcrID)
    setSiteMode('ePCR')
  }

  useEffect(() => {
    const timer = setTimeout(() => setPulseWorkstationButton(false), 5000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!workstationMode || workstationLocked) return undefined

    const markActivity = () => {
      setLastActivity(Date.now())
      setLockCountdown(WORKSTATION_LOCK_SECONDS)
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click']
    events.forEach((event) => window.addEventListener(event, markActivity))

    return () => {
      events.forEach((event) => window.removeEventListener(event, markActivity))
    }
  }, [workstationLocked, workstationMode])

  useEffect(() => {
    if (!workstationMode || workstationLocked) return undefined

    const timer = setInterval(() => {
      const secondsRemaining = Math.max(
        0,
        WORKSTATION_LOCK_SECONDS -
          Math.floor((Date.now() - lastActivity) / 1000),
      )
      setLockCountdown(secondsRemaining)

      if (secondsRemaining <= 0) {
        setWorkstationLocked(true)
        if (locationID) {
          API.post(
            `/commandcontrol/treatment-locations/${locationID}/workstation/lock`,
          ).catch(() => {})
        }
      }
    }, 500)

    return () => clearInterval(timer)
  }, [lastActivity, locationID, workstationLocked, workstationMode])

  const loadTemplate = async () => {
    if (locationID && canConfigure) {
      await Promise.all(
        templateZones.map((zone, index) =>
          API.post(`/commandcontrol/treatment-locations/${locationID}/zones`, {
            name: zone.name,
            type: zone.type === 'Waiting' ? 'Waiting Room' : zone.type,
            maxCapacity: zone.capacity,
            capacityType: zone.capacity > 1 ? 'Multiple' : 'Single',
            status: 'Available',
            sortOrder: index,
          }),
        ),
      )
      refresh?.()
      return
    }

    setZones(templateZones)
  }

  const createNewWaitingPatient = async () => {
    if (!locationID || creatingPatient) return

    setCreatingPatient(true)

    try {
      const res = await API.post(
        `/commandcontrol/treatment-locations/${locationID}/new-patient`,
      )

      if (res.status === 200) {
        refresh?.()
        openEPCR(res.data.epcrID)
      }
    } finally {
      setCreatingPatient(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(35,154,164,0.12), transparent 28%), linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)',
      }}
    >
      <Box
        sx={{
          filter: boardLocked ? 'blur(8px)' : 'none',
          pointerEvents: boardLocked ? 'none' : 'auto',
          userSelect: boardLocked ? 'none' : 'auto',
        }}
      >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          color: 'white',
          background:
            'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
          boxShadow: '0 10px 32px rgba(15,23,42,0.18)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Hotel />
            </Box>

            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Treatment Location
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="h5" fontWeight={900} color="white" noWrap>
                  {title}
                </Typography>
                {status && status !== 'Unknown' && (
                  <Chip
                    size="small"
                    label={status}
                    color={centreStatusColor[status] || 'default'}
                    sx={{ borderRadius: 999, fontWeight: 800 }}
                  />
                )}
                {sync && (
                  <Chip
                    size="small"
                    icon={sync.icon}
                    label={sync.label}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.14)',
                      '& .MuiChip-icon': { color: sync.color },
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ md: 'center' }}
          >
            <Chip
              label={`${stats.totalPatients} patients`}
              sx={{
                color: 'white',
                backgroundColor: 'anPurple.main',
              }}
            />
            {stats.pending > 0 && (
              <Chip label={`${stats.pending} pending`} color="warning" />
            )}
            <Chip label={`${stats.available} available`} color="success" />
            <Chip label={`${stats.occupied} occupied`} color="error" />
            {stats.cleaning + stats.outOfOrder > 0 && (
              <Chip
                label={`${stats.cleaning + stats.outOfOrder} unavailable`}
                color="info"
              />
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
          >
            {workstationMode && (
              <>
                <Stack textAlign="right">
                  <Typography fontWeight={900} color="white">
                    {displayUser?.name || 'Locked'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="white"
                    sx={{ opacity: 0.72 }}
                  >
                    {displayUser
                      ? `${displayUser.role || 'Clinician'}${
                          lockCountdown <= 30
                            ? ` - locks in ${lockCountdown}s`
                            : ''
                        }`
                      : 'Select user'}
                  </Typography>
                </Stack>

                <Avatar src={displayUser?.photograph}>
                  {displayUser?.name?.[0] || <Lock />}
                </Avatar>

                <Button
                  variant="contained"
                  startIcon={<SwapHoriz />}
                  onClick={() => {
                    setOpenSwitchUser(true)
                  }}
                  sx={{
                    borderRadius: 999,
                    backgroundColor: 'rgba(255,255,255,0.16)',
                    color: 'white',
                    fontWeight: 900,
                  }}
                >
                  Switch
                </Button>
              </>
            )}

            {!workstationMode && canOpenWorkstation && locationID && (
              <Button
                variant="contained"
                startIcon={<Tv />}
                onClick={() => setWorkstationPromptOpen(true)}
                sx={{
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 900,
                  animation: pulseWorkstationButton
                    ? 'workstationPulse 1.25s ease-in-out 4'
                    : 'none',
                  '@keyframes workstationPulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(255,255,255,0.5)' },
                    '70%': { boxShadow: '0 0 0 12px rgba(255,255,255,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(255,255,255,0)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              >
                Open as Workstation
              </Button>
            )}

            {onExit && (
              <Button
                variant="contained"
                startIcon={<ExitToApp />}
                onClick={onExit}
                sx={{
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 900,
                }}
              >
                Exit
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={900}>
              Live Bed Board
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track patients, zones, bays and welfare areas in real time.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{
              // Phones: wrap the action buttons instead of squashing three
              // of them into one row with wrapped labels.
              '& .MuiButton-root': {
                whiteSpace: 'nowrap',
                flex: { xs: '1 1 auto', md: '0 0 auto' },
              },
            }}
          >
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={createNewWaitingPatient}
              disabled={creatingPatient || !waitingRoomHasCapacity}
            >
              {creatingPatient
                ? 'Creating...'
                : waitingRoomHasCapacity
                  ? 'New Patient'
                  : 'Waiting Room Full'}
            </Button>

            {canConfigure && (
              <Button
                variant="outlined"
                startIcon={<LocalHospital />}
                onClick={() => setOpenConfigureCentre(true)}
              >
                Configure Centre
              </Button>
            )}

            {zones.length === 0 && (
              <Button variant="contained" onClick={loadTemplate}>
                Load Standard Template
              </Button>
            )}

            {canConfigure && (
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setOpenConfigureZones(true)}
              >
                Configure Zones
              </Button>
            )}
          </Stack>
        </Stack>

        <Box>
          {loading ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                textAlign: 'center',
                border: '1px solid rgba(15,23,42,0.08)',
              }}
            >
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={24} />
                <Typography fontWeight={900}>
                  Loading treatment centre...
                </Typography>
              </Stack>
            </Paper>
          ) : zones.length === 0 ? (
            <>
              <PendingPatientsPanel
                assignments={pendingPatientAssignments}
                zones={zones}
                onPlaced={refresh}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  textAlign: 'center',
                  border: '1px dashed rgba(15,23,42,0.2)',
                }}
              >
                <Typography fontWeight={900}>No zones configured</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Add bays manually or load a standard treatment centre template.
                </Typography>
                <Button variant="contained" onClick={loadTemplate}>
                  Load Template
                </Button>
              </Paper>
            </>
          ) : (
            <>
              <PendingPatientsPanel
                assignments={pendingPatientAssignments}
                zones={zones}
                onPlaced={refresh}
              />
              <Grid container spacing={2}>
                {clinicalZones.map((zone) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    xl={3}
                    sx={{ display: 'flex' }}
                    key={zone.id}
                  >
                    <TreatmentZoneCard
                      zone={zone}
                      onClick={(selected) => setSelectedZone(selected)}
                    />
                  </Grid>
                ))}
                {waitingZone && (
                  <Grid item xs={12}>
                    <WaitingRoomCard
                      patients={waitingZone.patients || []}
                      onPatientClick={(patient) => {
                        setSelectedZone(waitingZone)
                      }}
                      onAddPatient={
                        waitingRoomHasCapacity ? createNewWaitingPatient : null
                      }
                      canAddPatient={waitingRoomHasCapacity}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>
      </Box>
      </Box>

      <ZoneDrawer
        zone={selectedZone}
        open={!!selectedZone}
        onClose={() => setSelectedZone(null)}
        onOpenPatient={(patient) => {
          setSelectedZone(null)
          openEPCR(patient.epcrID)
        }}
      />
      {workstationMode && (
        <SwitchUserDialog
          open={workstationLocked || openSwitchUser}
          onClose={() => setOpenSwitchUser(false)}
          onSwitch={(user) => {
            setCurrentUser(user)
            setWorkstationLocked(false)
            setOpenSwitchUser(false)
            setLastActivity(Date.now())
            setLockCountdown(WORKSTATION_LOCK_SECONDS)
          }}
          users={assignedUsers}
          locationID={locationID}
          locked={workstationLocked}
          countdown={lockCountdown}
        />
      )}
      <Dialog
        open={workstationPromptOpen}
        onClose={() => setWorkstationPromptOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Open as Workstation</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Workstation mode turns this screen into a shared bed board for
              the treatment location — for example a tablet or screen left at
              the centre for the whole team.
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: '1.25em' }}>
                <li>
                  The board starts locked. Assigned staff sign in with their
                  4-digit PIN (or password) and can switch user at any time.
                </li>
                <li>
                  After {WORKSTATION_LOCK_SECONDS / 60} minutes of inactivity
                  the screen locks itself automatically.
                </li>
                <li>
                  Actions are recorded against the staff member currently
                  signed in to the workstation.
                </li>
              </ul>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use Exit on the workstation to return to your personal view.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkstationPromptOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Tv />}
            onClick={() => {
              setWorkstationPromptOpen(false)
              navigate(`/treatmentworkstations/${locationID}`)
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <ConfigureCentreDialog
        open={openConfigureCentre}
        onClose={() => setOpenConfigureCentre(false)}
        centre={centre}
        onSaved={refresh}
      />
      <ConfigureZonesDialog
        open={openConfigureZones}
        onClose={() => setOpenConfigureZones(false)}
        locationID={locationID}
        zones={zones}
        onSaved={refresh}
      />
    </Box>
  )
}

export default TreatmentCentreWorkspace
